import useZoomPanEditor from "../../../../components/zoomPanEditor/useZoomPanEditor";
import RandomizeHelper from "../../../../helpers/RandomizeHelper";
import KeyboardHelper from "../../../../helpers/KeyboardHelper";
import useCustomEffect from "../../../../hooks/useCustomEffect";
import { getInitLabelItemData } from "../label_items/LabelItems";
import LabelEditorView from "./LabelEditorView";

function LabelEditor({ printerData, setPrinterData, fields, images }) {
  const zoomPanController = useZoomPanEditor({
    onlyAllowBackgroundInteraction: true,
  });

  /**
   * UseEffect for window events
   */
  useCustomEffect(() => {
    window.addEventListener("click", onWindowClick);
    window.addEventListener("keydown", onKeyPress);
    return () => {
      window.removeEventListener("click", onWindowClick);
      window.removeEventListener("keydown", onKeyPress);
    };
  }, [printerData.label_items]);

  /**
   * This method will handle the position change of an item.
   * @param {any} itemId
   * @param {object} newPos
   */
  function onItemPosChange(itemId, newPos) {
    setPrinterData((prevData) => ({
      ...prevData,
      label_items: prevData.label_items.map((item) =>
        item.id === itemId
          ? {
              ...item,
              position: newPos,
            }
          : item,
      ),
    }));
  }

  /**
   * This method will add a new item to the label.
   * @param {string} type
   */
  function onItemAdd(type) {
    setPrinterData((prevData) => ({
      ...prevData,
      label_selected: false,
      label_items: [
        ...prevData.label_items.map((item) => ({ ...item, selected: false })),
        {
          id: RandomizeHelper.getUUID(),
          type: type,
          selected: true,
          position: { x: 0, y: 0 },
          width: 30,
          height: 10,
          ...getInitLabelItemData(type),
        },
      ],
    }));
  }

  /**
   * This method will handle the selection of an item.
   * @param {any} itemId
   */
  function onItemSelect(itemId) {
    setPrinterData((prevData) => ({
      ...prevData,
      label_items: prevData.label_items.map((item) =>
        item.id === itemId
          ? { ...item, selected: true }
          : { ...item, selected: false },
      ),
    }));
  }

  /**
   * This method will handle the window click event
   * @param {event} e
   */
  function onWindowClick(e) {
    //Only handle the click if the editor contains the target
    if (!zoomPanController.editorRef.current?.contains(e.target)) {
      return;
    }

    // Select or deselect items based on the click target
    setPrinterData((prevData) => ({
      ...prevData,
      label_selected: e.target.id.includes("[PRINT_LABEL]"),
      label_items: e.target.id.includes("[PRINT_LABEL_ITEM]")
        ? prevData.label_items
        : prevData.label_items.map((item) => ({ ...item, selected: false })),
    }));
  }

  /**
   * This method will handle the key press event
   * @param {event} e
   */
  function onKeyPress(e) {
    //Don't delete items while the user is typing in an input
    if (KeyboardHelper.isEditableTarget(e.target)) return;

    if (e.key === "Delete") {
      setPrinterData((prevData) => ({
        ...prevData,
        label_items: prevData.label_items.filter((item) => !item.selected),
      }));
    }
  }

  return (
    <LabelEditorView
      zoomPanController={zoomPanController}
      printerData={printerData}
      onItemPosChange={onItemPosChange}
      onItemAdd={onItemAdd}
      onItemSelect={onItemSelect}
      fields={fields}
      images={images}
    />
  );
}

export default LabelEditor;
